import { useMemo, useCallback } from "react";
import { SlideDrawer } from "@/components/ui/slide-drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Database, GitMerge, Flag, Table, Plus, Trash2, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ExpressionNode, ExpressionNodeData, InputNodeData, HubNodeData, EmitOutputNodeData, TableNodeData, MembershipNodeData } from "@/types/decision-graph";
import type { DecisionDefinition, EmitRule, ScoreBand } from "@/types/decision-definition-schema";
import { generateNodeId } from "@/types/decision-definition-schema";

interface DecisionNodeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNode: ExpressionNode | null;
  definition: DecisionDefinition;
  nodes?: ExpressionNode[];
  edges?: { source: string; target: string; targetHandle?: string | null; sourceHandle?: string | null }[];
  onNodeDataChange: (nodeId: string, data: Partial<ExpressionNodeData>) => void;
  onEmitRuleChange?: (emitRuleId: string, updates: Partial<EmitRule>) => void;
  onDefinitionChange?: (definition: DecisionDefinition) => void;
  readOnly?: boolean;
}

function NodeIcon({ nodeType }: { nodeType: string }) {
  switch (nodeType) {
    case "input": return <Database size={16} className="text-emerald-600" />;
    case "hub": return <GitMerge size={16} className="text-purple-600" />;
    case "emitOutput": return <Flag size={16} className="text-amber-600" />;
    case "table": return <Table size={16} className="text-blue-600" />;
    case "membership": return <ListChecks size={16} className="text-teal-600" />;
    default: return null;
  }
}

function InputNodeEditor({
  data,
  onDataChange,
  readOnly,
}: {
  data: InputNodeData;
  onDataChange: (updates: Partial<InputNodeData>) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Input Schema Reference</Label>
        <Input
          value={data.inputSchemaRef || ""}
          onChange={(e) => onDataChange({ inputSchemaRef: e.target.value })}
          placeholder="e.g. schema.applicant_input"
          disabled={readOnly}
        />
        <p className="text-xs text-muted-foreground">
          Link to an existing schema capability that defines the input structure
        </p>
      </div>
      <div className="space-y-2">
        <Label>Detected Fact Paths</Label>
        <div className="rounded-md border p-2 space-y-1 max-h-[200px] overflow-y-auto">
          {data.factPaths.length === 0 ? (
            <p className="text-xs text-muted-foreground">No fact references detected</p>
          ) : (
            data.factPaths.map((path) => (
              <div
                key={path}
                className="text-xs font-mono bg-slate-50 px-2 py-1 rounded"
              >
                {path}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function HubNodeEditor({
  data,
  onDataChange,
  definition,
  onDefinitionChange,
  readOnly,
}: {
  data: HubNodeData;
  onDataChange: (updates: Partial<HubNodeData>) => void;
  definition: DecisionDefinition;
  onDefinitionChange?: (definition: DecisionDefinition) => void;
  readOnly?: boolean;
}) {
  const mode = data.decisionMode || "tri_state";
  const emitRules = definition.emitRules || [];
  const scoreBands = data.scoreBands || [];

  const addOutcome = useCallback(() => {
    if (!onDefinitionChange) return;
    const newRule: EmitRule = {
      id: generateNodeId(),
      label: `Outcome ${emitRules.length + 1}`,
      when: { type: "literal", value: true },
      emit: { outcome: `outcome_${emitRules.length + 1}`, flags: [], reasons: [], obligations: [], data: {} },
      stopProcessing: false,
    };
    onDefinitionChange({
      ...definition,
      emitRules: [...emitRules, newRule],
    });
  }, [definition, emitRules, onDefinitionChange]);

  const removeOutcome = useCallback(
    (ruleId: string) => {
      if (!onDefinitionChange) return;
      onDefinitionChange({
        ...definition,
        emitRules: emitRules.filter((r) => r.id !== ruleId),
      });
    },
    [definition, emitRules, onDefinitionChange]
  );

  const renameOutcome = useCallback(
    (ruleId: string, label: string) => {
      if (!onDefinitionChange) return;
      onDefinitionChange({
        ...definition,
        emitRules: emitRules.map((r) =>
          r.id === ruleId ? { ...r, label } : r
        ),
      });
    },
    [definition, emitRules, onDefinitionChange]
  );

  const addScoreBand = useCallback(() => {
    const last = scoreBands[scoreBands.length - 1];
    const newBand: ScoreBand = {
      min: last ? last.max + 1 : 0,
      max: last ? last.max + 50 : 50,
      label: `Band ${scoreBands.length + 1}`,
    };
    const updatedBands = [...scoreBands, newBand];
    onDataChange({ scoreBands: updatedBands });
    if (onDefinitionChange) {
      const newRule: EmitRule = {
        id: generateNodeId(),
        label: newBand.label,
        when: { type: "literal", value: true },
        emit: {
          outcome: newBand.label.toLowerCase().replace(/\s+/g, "_"),
          band: newBand.label,
          score: newBand.min,
          flags: [],
          reasons: [],
          obligations: [],
          data: {},
        },
        stopProcessing: false,
      };
      onDefinitionChange({
        ...definition,
        emitRules: [...emitRules, newRule],
        scoreBands: updatedBands,
      });
    }
  }, [scoreBands, emitRules, definition, onDataChange, onDefinitionChange]);

  const removeScoreBand = useCallback(
    (index: number) => {
      const updatedBands = scoreBands.filter((_, i) => i !== index);
      onDataChange({ scoreBands: updatedBands });
      if (onDefinitionChange && emitRules.length > index) {
        onDefinitionChange({
          ...definition,
          emitRules: emitRules.filter((_, i) => i !== index),
          scoreBands: updatedBands,
        });
      }
    },
    [scoreBands, emitRules, definition, onDataChange, onDefinitionChange]
  );

  const updateScoreBand = useCallback(
    (index: number, updates: Partial<ScoreBand>) => {
      const updatedBands = scoreBands.map((b, i) =>
        i === index ? { ...b, ...updates } : b
      );
      onDataChange({ scoreBands: updatedBands });
      if (onDefinitionChange) {
        const band = { ...scoreBands[index], ...updates };
        const updatedRules = emitRules.map((r, i) =>
          i === index ? { ...r, label: band.label } : r
        );
        onDefinitionChange({
          ...definition,
          emitRules: updatedRules,
          scoreBands: updatedBands,
        });
      }
    },
    [scoreBands, emitRules, definition, onDataChange, onDefinitionChange]
  );

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-purple-50 px-3 py-2 text-xs text-purple-800">
        The Decision Hub is where all logic converges. Output handles on the
        node change with the selected decision mode.
      </div>
      <div className="space-y-2">
        <Label>Decision Mode</Label>
        <Select
          value={mode}
          onValueChange={(v) =>
            onDataChange({ decisionMode: v as HubNodeData["decisionMode"] })
          }
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="boolean">Boolean (yes / no)</SelectItem>
            <SelectItem value="tri_state">Tri-State (yes / no / unknown)</SelectItem>
            <SelectItem value="multi_outcome">Multi-Outcome</SelectItem>
            <SelectItem value="scorecard">Scorecard</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {mode === "boolean" && "Two outputs: Approve and Decline"}
          {mode === "tri_state" && "Three outputs: Approve, Review, and Decline"}
          {mode === "multi_outcome" && "One output handle per named outcome"}
          {mode === "scorecard" && "One output handle per score band range"}
        </p>
      </div>

      {/* Multi-outcome: manage named outcomes */}
      {mode === "multi_outcome" && (
        <div className="space-y-3 border-t pt-3">
          <div className="flex items-center justify-between">
            <Label>Outcomes ({emitRules.length})</Label>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={addOutcome}
              >
                <Plus size={12} /> Add Outcome
              </Button>
            )}
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {emitRules.map((rule, i) => (
              <div
                key={rule.id}
                className="flex items-center gap-2 rounded-md border px-2 py-1.5"
              >
                <div
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{
                    background:
                      ["#22c55e", "#f59e0b", "#ef4444", "#3b82f6", "#a855f7", "#14b8a6"][
                        i % 6
                      ],
                  }}
                />
                <Input
                  className="h-7 flex-1 text-xs"
                  value={rule.label || ""}
                  onChange={(e) => renameOutcome(rule.id, e.target.value)}
                  disabled={readOnly}
                  placeholder="Outcome name"
                />
                {!readOnly && emitRules.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                    onClick={() => removeOutcome(rule.id)}
                  >
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scorecard: manage score bands */}
      {mode === "scorecard" && (
        <div className="space-y-3 border-t pt-3">
          <div className="flex items-center justify-between">
            <Label>Score Bands ({scoreBands.length})</Label>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={addScoreBand}
              >
                <Plus size={12} /> Add Band
              </Button>
            )}
          </div>
          {scoreBands.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No score bands defined. Add bands to create output ports for each
              score range.
            </p>
          )}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {scoreBands.map((band, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-md border px-2 py-1.5"
              >
                <div
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{
                    background:
                      ["#22c55e", "#f59e0b", "#ef4444", "#3b82f6", "#a855f7", "#14b8a6"][
                        i % 6
                      ],
                  }}
                />
                <Input
                  className="h-7 w-16 text-xs"
                  type="number"
                  value={band.min}
                  onChange={(e) =>
                    updateScoreBand(i, { min: Number(e.target.value) })
                  }
                  disabled={readOnly}
                  placeholder="Min"
                />
                <span className="text-xs text-slate-400">–</span>
                <Input
                  className="h-7 w-16 text-xs"
                  type="number"
                  value={band.max}
                  onChange={(e) =>
                    updateScoreBand(i, { max: Number(e.target.value) })
                  }
                  disabled={readOnly}
                  placeholder="Max"
                />
                <Input
                  className="h-7 flex-1 text-xs"
                  value={band.label}
                  onChange={(e) => updateScoreBand(i, { label: e.target.value })}
                  disabled={readOnly}
                  placeholder="Band label"
                />
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                    onClick={() => removeScoreBand(i)}
                  >
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmitOutputNodeEditor({
  data,
  emitRule,
  onDataChange,
  onEmitRuleChange,
  readOnly,
}: {
  data: EmitOutputNodeData;
  emitRule?: EmitRule;
  onDataChange: (updates: Partial<EmitOutputNodeData>) => void;
  onEmitRuleChange?: (updates: Partial<EmitRule>) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Label</Label>
        <Input
          value={data.label || ""}
          onChange={(e) => onDataChange({ label: e.target.value })}
          placeholder="Output label"
          disabled={readOnly}
        />
      </div>
      <div className="space-y-2">
        <Label>Outcome</Label>
        <Input
          value={data.outcome || ""}
          onChange={(e) => onDataChange({ outcome: e.target.value })}
          placeholder="e.g. approved, declined"
          disabled={readOnly}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label htmlFor="stop-processing">Stop Processing</Label>
        <Switch
          id="stop-processing"
          checked={data.stopProcessing}
          onCheckedChange={(checked) => onDataChange({ stopProcessing: checked })}
          disabled={readOnly}
        />
      </div>
      {emitRule && (
        <>
          <div className="space-y-2">
            <Label>Reasons ({emitRule.emit?.reasons?.length || 0})</Label>
            <div className="text-xs text-muted-foreground">
              Edit reasons in the full emit rule editor
            </div>
          </div>
          <div className="space-y-2">
            <Label>Flags ({emitRule.emit?.flags?.length || 0})</Label>
            <div className="text-xs text-muted-foreground">
              Edit flags in the full emit rule editor
            </div>
          </div>
          <div className="space-y-2">
            <Label>Obligations ({emitRule.emit?.obligations?.length || 0})</Label>
            <div className="text-xs text-muted-foreground">
              Edit obligations in the full emit rule editor
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TableNodeEditor({
  data,
  definition,
}: {
  data: TableNodeData;
  definition: DecisionDefinition;
}) {
  const ruleElement = useMemo(() => {
    if (!data.ruleElementId) return undefined;
    return definition.ruleElements?.find((r) => r.id === data.ruleElementId);
  }, [data.ruleElementId, definition.ruleElements]);

  const tableData = useMemo(() => {
    if (!ruleElement?.expression) return { columns: [] as string[], rows: [] as string[][] };
    const expr = ruleElement.expression;
    const columns: string[] = [];
    const rows: string[][] = [];

    if (expr.type === "and" || expr.type === "or") {
      const children = expr.children || [];
      for (const child of children) {
        const row: string[] = [];
        if (child.type === "comparison" && child.left && child.right) {
          const field = child.left.type === "field_ref" ? (child.left.path || "field") : "expr";
          const op = child.operator || "==";
          const val = child.right.type === "literal" ? String(child.right.value ?? "") : "expr";
          if (!columns.includes(field)) columns.push(field);
          const colIdx = columns.indexOf(field);
          while (row.length <= colIdx) row.push("");
          row[colIdx] = `${op} ${val}`;
        } else {
          const label = child.label || child.type;
          if (!columns.includes(label)) columns.push(label);
          const colIdx = columns.indexOf(label);
          while (row.length <= colIdx) row.push("");
          row[colIdx] = "✓";
        }
        rows.push(row);
      }
    }

    // Pad rows to column count
    for (const row of rows) {
      while (row.length < columns.length) row.push("");
    }

    return { columns, rows };
  }, [ruleElement]);

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
        Decision table with {data.rowCount} rules across {data.columnCount} conditions.
      </div>

      {ruleElement && (
        <div className="space-y-2">
          <Label>Rule Element</Label>
          <div className="text-sm font-medium">{ruleElement.label}</div>
          {ruleElement.legislationReference?.section && (
            <div className="text-xs text-muted-foreground">
              Ref: §{ruleElement.legislationReference.section}
              {ruleElement.legislationReference.subsection && `.${ruleElement.legislationReference.subsection}`}
            </div>
          )}
        </div>
      )}

      {tableData.columns.length > 0 ? (
        <div className="overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                {tableData.columns.map((col) => (
                  <th key={col} className="px-3 py-2 text-left font-medium text-slate-600">
                    {col}
                  </th>
                ))}
                <th className="px-3 py-2 text-left font-medium text-slate-600">Result</th>
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 font-mono">
                      {cell || <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                      pass
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          No table data available. The underlying expression does not decompose into table rows.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md border px-3 py-2">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">Rows</div>
          <div className="text-lg font-semibold">{data.rowCount}</div>
        </div>
        <div className="rounded-md border px-3 py-2">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">Columns</div>
          <div className="text-lg font-semibold">{data.columnCount}</div>
        </div>
      </div>

      {ruleElement?.inputsRequired && ruleElement.inputsRequired.length > 0 && (
        <div className="space-y-2">
          <Label>Required Inputs</Label>
          <div className="flex flex-wrap gap-1">
            {ruleElement.inputsRequired.map((input) => (
              <span
                key={input}
                className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700"
              >
                {input}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MembershipNodeEditor({
  data,
  node,
  edges,
  nodes,
}: {
  data: MembershipNodeData;
  node: ExpressionNode;
  edges: { source: string; target: string; targetHandle?: string | null; sourceHandle?: string | null }[];
  nodes: ExpressionNode[];
}) {
  const isIn = data.operator === "IN";
  const setSize = data.setSize || 0;

  const operandSource = useMemo(() => {
    const edge = edges.find((e) => e.target === node.id && e.targetHandle === "operand");
    if (!edge) return null;
    return nodes.find((n) => n.id === edge.source) || null;
  }, [edges, node.id, nodes]);

  const setElements = useMemo(() => {
    const elements: { index: number; source: ExpressionNode | null }[] = [];
    for (let i = 0; i < setSize; i++) {
      const edge = edges.find((e) => e.target === node.id && e.targetHandle === `set-${i}`);
      const source = edge ? nodes.find((n) => n.id === edge.source) || null : null;
      elements.push({ index: i, source });
    }
    return elements;
  }, [edges, node.id, setSize, nodes]);

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-teal-50 px-3 py-2 text-xs text-teal-800">
        Set membership test: checks whether a value is {isIn ? "contained in" : "not contained in"} a set of elements.
      </div>

      <div className="space-y-2">
        <Label>Operator</Label>
        <div className={cn(
          "inline-block rounded px-2 py-0.5 text-sm font-semibold",
          isIn ? "bg-teal-100 text-teal-700" : "bg-rose-100 text-rose-700"
        )}>
          {data.operator}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Operand (value being tested)</Label>
        <div className="rounded-md border px-3 py-2 text-sm">
          {operandSource ? (
            <span className="font-mono text-xs">
              {operandSource.data.label || operandSource.data.nodeType}
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">Not connected</span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Set Elements ({setSize})</Label>
        {setSize > 0 ? (
          <div className="overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Input</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Value / Path</th>
                </tr>
              </thead>
              <tbody>
                {setElements.map((el) => (
                  <tr key={el.index} className="border-b last:border-0 hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-muted-foreground">{el.index + 1}</td>
                    <td className="px-3 py-2 font-mono">
                      {el.source?.data.label || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {el.source ? (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium">
                          {el.source.data.nodeType}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono">
                      {el.source?.data.nodeType === "literal"
                        ? String((el.source.data as { value?: unknown }).value ?? "")
                        : el.source?.data.nodeType === "field"
                          ? (el.source.data as { path?: string }).path || ""
                          : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            No set elements. Add elements using the + button on the node.
          </div>
        )}
      </div>
    </div>
  );
}

function LogicNodeEditor({
  node,
}: {
  node: ExpressionNode;
}) {
  const data = node.data;
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
        Logic node of type <span className="font-mono font-medium">{data.nodeType}</span>.
        Edit connections by dragging handles on the graph canvas.
      </div>
      <div className="space-y-2">
        <Label>Label</Label>
        <div className="text-sm font-medium">{data.label}</div>
      </div>
      {data.nodeType === "field" && "path" in data && (
        <div className="space-y-2">
          <Label>Path</Label>
          <div className="text-sm font-mono">{(data as { path?: string }).path}</div>
        </div>
      )}
      {data.nodeType === "comparison" && "operator" in data && (
        <div className="space-y-2">
          <Label>Operator</Label>
          <div className="text-sm font-mono">{(data as { operator?: string }).operator}</div>
        </div>
      )}
      {data.nodeType === "literal" && "value" in data && (
        <div className="space-y-2">
          <Label>Value</Label>
          <div className="text-sm font-mono">{String((data as { value?: unknown }).value)}</div>
        </div>
      )}
    </div>
  );
}

export function DecisionNodeDrawer({
  isOpen,
  onClose,
  selectedNode,
  definition,
  nodes = [],
  edges = [],
  onNodeDataChange,
  onEmitRuleChange,
  onDefinitionChange,
  readOnly = false,
}: DecisionNodeDrawerProps) {
  const nodeType = selectedNode?.data?.nodeType as string | undefined;

  const emitRule = useMemo(() => {
    if (nodeType !== "emitOutput" || !selectedNode) return undefined;
    const d = selectedNode.data as unknown as EmitOutputNodeData;
    return definition.emitRules?.find((r) => r.id === d.emitRuleId);
  }, [nodeType, selectedNode, definition.emitRules]);

  const title = useMemo(() => {
    switch (nodeType) {
      case "input": return "Input Configuration";
      case "hub": return "Decision Hub";
      case "emitOutput": return "Output Configuration";
      case "table": return "Decision Table";
      case "membership": return "Set Membership";
      default: return "Node Details";
    }
  }, [nodeType]);

  const description = useMemo(() => {
    switch (nodeType) {
      case "input": return "Configure input schema and fact references";
      case "hub": return "Central decision convergence point";
      case "emitOutput": return "Configure output outcome, reasons, and flags";
      case "table": return "Table-based decision logic";
      case "membership": return "Tests whether a value is in a set of elements";
      default: return "View and edit node properties";
    }
  }, [nodeType]);

  if (!selectedNode) return null;

  return (
    <SlideDrawer isOpen={isOpen} onClose={onClose}>
        <div className="px-6 pt-6 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <NodeIcon nodeType={nodeType || ""} />
            <h2 className="text-lg font-semibold leading-none tracking-tight">{title}</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">{description}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {nodeType === "input" && (
            <InputNodeEditor
              data={selectedNode.data as unknown as InputNodeData}
              onDataChange={(updates) =>
                onNodeDataChange(selectedNode.id, updates as Partial<ExpressionNodeData>)
              }
              readOnly={readOnly}
            />
          )}
          {nodeType === "hub" && (
            <HubNodeEditor
              data={selectedNode.data as unknown as HubNodeData}
              onDataChange={(updates) =>
                onNodeDataChange(selectedNode.id, updates as Partial<ExpressionNodeData>)
              }
              definition={definition}
              onDefinitionChange={onDefinitionChange}
              readOnly={readOnly}
            />
          )}
          {nodeType === "emitOutput" && (
            <EmitOutputNodeEditor
              data={selectedNode.data as unknown as EmitOutputNodeData}
              emitRule={emitRule}
              onDataChange={(updates) =>
                onNodeDataChange(selectedNode.id, updates as Partial<ExpressionNodeData>)
              }
              onEmitRuleChange={
                emitRule && onEmitRuleChange
                  ? (updates) => onEmitRuleChange(emitRule.id, updates)
                  : undefined
              }
              readOnly={readOnly}
            />
          )}
          {nodeType === "table" && (
            <TableNodeEditor
              data={selectedNode.data as unknown as TableNodeData}
              definition={definition}
            />
          )}
          {nodeType === "membership" && (
            <MembershipNodeEditor
              data={selectedNode.data as unknown as MembershipNodeData}
              node={selectedNode}
              edges={edges}
              nodes={nodes}
            />
          )}
          {nodeType && !["input", "hub", "emitOutput", "table", "membership"].includes(nodeType) && (
            <LogicNodeEditor node={selectedNode} />
          )}
        </div>
        <div className="border-t px-6 py-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
    </SlideDrawer>
  );
}
